/**
 * Safe expression evaluator for ${variable} syntax
 * Supports basic math and comparisons without eval()
 */

import type { Variables } from '../types/interfaces';

const ALLOWED_OPERATORS: string[] = ['+', '-', '*', '/', '%', '>', '<', '>=', '<=', '==', '!=', '&&', '||', '!'];
const ALLOWED_FUNCTIONS: string[] = ['Math.min', 'Math.max', 'Math.abs', 'Math.floor', 'Math.ceil', 'Math.round', 'Math.sqrt'];
const ALLOWED_METHODS: string[] = ['toFixed', 'toPrecision', 'toExponential', 'toString'];

/** Math functions available in expressions */
interface MathFunctions {
  min: typeof Math.min;
  max: typeof Math.max;
  abs: typeof Math.abs;
  floor: typeof Math.floor;
  ceil: typeof Math.ceil;
  round: typeof Math.round;
  sqrt: typeof Math.sqrt;
}

/** Result of expression validation */
export interface ValidationResult {
  valid: boolean;
  error: string | null;
}

/**
 * Evaluate template expression like "${x + y}"
 * @param template - Template string
 * @param context - Variable values
 * @returns Evaluated result
 */
export function evaluateExpression(template: string | number | boolean, context: Variables): string | number | boolean {
  if (typeof template !== 'string') {
    return template;
  }

  // If the entire template is just a single expression, return the raw value (preserving type)
  const singleExprMatch = /^\$\{([^}]+)\}$/.exec(template);
  if (singleExprMatch) {
    try {
      return evaluateSafeExpression(singleExprMatch[1].trim(), context);
    } catch (error) {
      console.warn(`Expression evaluation failed: ${singleExprMatch[1]}`, error);
      return template;
    }
  }

  // Replace ${...} with evaluated values (convert to string for template substitution)
  return template.replace(/\$\{([^}]+)\}/g, (match: string, expr: string) => {
    try {
      return String(evaluateSafeExpression(expr.trim(), context));
    } catch (error) {
      console.warn(`Expression evaluation failed: ${expr}`, error);
      return match; // Return original if evaluation fails
    }
  });
}

/**
 * Evaluate a single expression safely
 * @param expr - Expression like "x + y" or "Math.min(a, b)"
 * @param context - Variable values
 * @returns Evaluated result
 */
function evaluateSafeExpression(expr: string, context: Variables): string | number | boolean {
  // Simple variable reference
  if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(expr)) {
    // Return 0 for undefined variables (common in initialization)
    return context[expr] !== undefined ? context[expr] : 0;
  }

  // Number literal
  if (/^-?\d+(\.\d+)?$/.test(expr)) {
    return parseFloat(expr);
  }

  // String literal
  if (/^["'].*["']$/.test(expr)) {
    return expr.slice(1, -1);
  }

  // Boolean literal
  if (expr === 'true') return true;
  if (expr === 'false') return false;

  // For now, use a very restricted Function constructor approach
  // This is still somewhat risky but much safer than eval()
  // TODO: Replace with proper expression parser library
  
  // Build safe context with only allowed variables, defaulting undefined to 0
  const safeContext: Record<string, string | number | boolean> = {};
  for (const key of Object.keys(context)) {
    safeContext[key] = context[key] !== undefined ? context[key] : 0;
  }
  
  // Extract variable names from expression and provide defaults for missing ones
  const varNamePattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g;
  let match: RegExpExecArray | null;
  while ((match = varNamePattern.exec(expr)) !== null) {
    const varName = match[1];
    // Skip Math functions and keywords
    if (!['Math', 'min', 'max', 'abs', 'floor', 'ceil', 'round', 'sqrt', 'true', 'false'].includes(varName)) {
      if (safeContext[varName] === undefined) {
        safeContext[varName] = 0; // Default missing variables to 0
      }
    }
  }
  
  // Add Math functions
  const mathFunctions: MathFunctions = {
    min: Math.min,
    max: Math.max,
    abs: Math.abs,
    floor: Math.floor,
    ceil: Math.ceil,
    round: Math.round,
    sqrt: Math.sqrt,
  };

  // Validate expression doesn't contain dangerous patterns
  const dangerousPattern = containsDangerousPatterns(expr);
  if (dangerousPattern) {
    throw new Error(`Unsafe expression: ${expr} (contains ${dangerousPattern})`);
  }

  try {
    // Create function with whitelisted context
    const keys = Object.keys(safeContext);
    const values = keys.map(k => safeContext[k]);
    
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    const func = new Function(...keys, 'Math', `return (${expr})`) as (...args: any[]) => any;
    return func(...values, mathFunctions);
  } catch (error) {
    throw new Error(`Failed to evaluate: ${expr}`);
  }
}

/**
 * Check for dangerous patterns in expression
 * @param expr - Expression to check
 * @returns Error message if dangerous, null otherwise
 */
function containsDangerousPatterns(expr: string): string | null {
  const dangerous: Array<{pattern: RegExp, name: string}> = [
    {pattern: /\beval\b/, name: 'eval'},
    {pattern: /\bFunction\b/, name: 'Function'},
    {pattern: /\brequire\b/, name: 'require'},
    {pattern: /\bimport\b/, name: 'import'},
    {pattern: /\bwindow\b/, name: 'window'},
    {pattern: /\bdocument\b/, name: 'document'},
    {pattern: /\bprocess\b/, name: 'process'},
    {pattern: /\b__\w+__\b/, name: 'dunder methods'},
    {pattern: /[;{}]/, name: 'statement separators'},
  ];

  for (const {pattern, name} of dangerous) {
    if (pattern.test(expr)) {
      return name;
    }
  }
  return null;
}

/**
 * Validate expression is safe to evaluate
 * @param expr - Expression to validate
 * @returns Validation result
 */
export function validateExpression(expr: string): ValidationResult {
  const dangerousPattern = containsDangerousPatterns(expr);
  if (dangerousPattern) {
    return {
      valid: false,
      error: `Expression contains unsafe pattern: ${dangerousPattern}`,
    };
  }

  // Check operators are whitelisted
  const tokens = expr.split(/[\s()[\],]/);
  for (const token of tokens) {
    if (token && !isAllowedToken(token)) {
      return {
        valid: false,
        error: `Token not allowed: ${token}`,
      };
    }
  }

  return { valid: true, error: null };
}

/**
 * Check if token is allowed
 * @param token - Token to check
 * @returns True if token is allowed
 */
function isAllowedToken(token: string): boolean {
  // Empty token
  if (!token) return true;
  
  // Variable name or Math
  if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(token)) return true;
  
  // Number
  if (/^-?\d+(\.\d+)?$/.test(token)) return true;
  
  // Operator
  if (ALLOWED_OPERATORS.includes(token)) return true;
  
  // Math function (Math.max, Math.min, etc)
  if (token.startsWith('Math.')) {
    const method = token.substring(5);
    return ['min', 'max', 'abs', 'floor', 'ceil', 'round', 'sqrt'].includes(method);
  }
  
  // Method call (e.g., .toFixed)
  if (ALLOWED_METHODS.some(m => token === m || token === `.${m}`)) return true;
  
  // Dot for method chaining
  if (token === '.') return true;

  return false;
}
